/**
 * Who is watching.
 *
 * Listed by name rather than counted, because a table where people can watch
 * invisibly is a table nobody wants to play at. Somebody who joins to watch
 * picks a name and a face on the way in for exactly this reason.
 *
 * The host, and only the host, can remove one. The id is match-local and
 * handed out in arrival order — the server never publishes a watcher's account
 * id to the room.
 */

import type { MatchView } from '../net/match.js';
import { avatarEmoji } from '../render/avatars.js';

export function Observers({
  view,
  onHostAction,
}: {
  view: MatchView;
  /** Omitted where removal makes no sense — a shared screen holds no authority. */
  onHostAction?: ((action: Record<string, unknown>) => void) | undefined;
}) {
  if (view.observers.length === 0) return null;

  const canRemove = view.isHost && onHostAction !== undefined;

  return (
    <div className="observers">
      <span className="field-label">
        Watching · {view.observers.length}
      </span>
      <ul className="observer-list">
        {view.observers.map((observer) => (
          <li key={observer.id}>
            <span aria-hidden="true">{avatarEmoji(observer.avatarId)}</span>
            <span className="observer-name">{observer.displayName}</span>
            {canRemove ? (
              <>
                {/*
                  Seating a watcher, offered here as well as in the lobby (L4).

                  A host mid-game had "remove" and nothing else, so the only
                  way to give a waiting friend a chair was to finish the round
                  and go back. Disabled rather than hidden when the table is
                  full: "there is no room" is a different answer from "you
                  cannot do this", and hiding it only makes the host look for
                  a control that is not there.
                */}
                <button
                  type="button"
                  className="text-button"
                  disabled={view.seats.length >= view.seatRange.max}
                  onClick={() => onHostAction({ action: 'MAKE_PLAYER', observerId: observer.id })}
                  title={
                    view.seats.length >= view.seatRange.max
                      ? 'The table is full'
                      : 'Give them a seat'
                  }
                >
                  Add to game
                </button>
                <button
                  type="button"
                  className="text-button danger"
                  onClick={() =>
                    onHostAction({ action: 'REMOVE_OBSERVER', observerId: observer.id })
                  }
                >
                  Remove
                </button>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The same fact, small enough to sit in a corner of a player's screen. */
export function ObserverCount({ view }: { view: MatchView }) {
  if (view.observers.length === 0) return null;
  return (
    <span className="pill" title={view.observers.map((o) => o.displayName).join(', ')}>
      👀 {view.observers.length}
    </span>
  );
}
